import axios from 'axios';
import { ApiSalesDashboardService } from 'services';
import {
	IBranchReceiptsData,
	IBranchReceiptsTotalsData,
	ICashBalanceData,
	ICashMovementsData,
	ICashMovement,
	ICashDeposit,
	ICashDepositPreview,
	ICashDepositsData,
	ICashTarget,
	CashBox,
	CashMovementType,
	CashMovementCategory,
	DepositStatus,
} from './types';

// ─── Envelope que usa la Support API ─────────────────────────────────────────

interface KpiEnvelope<T> {
	success: boolean;
	data: T;
	metadata?: { cachedAt?: string; [k: string]: unknown };
}

type Params = Record<string, string | number | boolean | undefined>;

// ─── Helpers internos de lectura ──────────────────────────────────────────────

async function kpiGet<T>(path: string, params?: Params): Promise<{ data: T; cachedAt: string | null }> {
	const cleanParams = params
		? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
		: undefined;
	const response = await ApiSalesDashboardService.get<KpiEnvelope<T>>(path, { params: cleanParams });
	if (!response.data.success) throw new Error(`API error: success=false on ${path}`);
	return { data: response.data.data, cachedAt: response.data.metadata?.cachedAt ?? null };
}

// ─── Helpers internos de escritura ───────────────────────────────────────────

async function kpiPost<T>(path: string, body?: unknown): Promise<T> {
	const response = await ApiSalesDashboardService.post<KpiEnvelope<T>>(path, body);
	if (!response.data.success) throw new Error(`API error: success=false on POST ${path}`);
	return response.data.data;
}

async function kpiPut<T>(path: string, body?: unknown): Promise<T> {
	const response = await ApiSalesDashboardService.put<KpiEnvelope<T>>(path, body);
	if (!response.data.success) throw new Error(`API error: success=false on PUT ${path}`);
	return response.data.data;
}

async function kpiPatch<T>(path: string, body?: unknown): Promise<T> {
	const response = await ApiSalesDashboardService.patch<KpiEnvelope<T>>(path, body);
	if (!response.data.success) throw new Error(`API error: success=false on PATCH ${path}`);
	return response.data.data;
}

/**
 * Extrae el mensaje de error de negocio de la API.
 * Para errores 400/409/422 expone el mensaje del backend tal cual (son mensajes para el usuario).
 * Para el resto usa mensajes genéricos.
 */
export function extractApiError(err: unknown): string {
	if (axios.isAxiosError(err)) {
		const status = err.response?.status;
		const data = err.response?.data as { message?: string } | undefined;
		if (status === 400 || status === 409 || status === 422) {
			return data?.message ?? `Error ${status}`;
		}
		if (status === 401) return 'Sin permisos para esta acción (401)';
		if (status === 403) return 'Acceso no autorizado (403)';
		if (status === 404) return 'Recurso no encontrado (404)';
		if (status && status >= 500) return `Error del servidor (${status}) — intentá de nuevo`;
		if (err.request) return 'No se pudo conectar con el servidor — verificá tu conexión';
	}
	if (err instanceof Error) return err.message;
	return 'Error inesperado';
}

/**
 * Extrae el detalle del error de validación de un depósito (422 con breakdown).
 * Retorna null si el error no es un 422 con breakdown.
 */
export function extractDepositValidationError(
	err: unknown,
): { message: string; breakdown?: Record<string, number> } | null {
	if (!axios.isAxiosError(err) || err.response?.status !== 422) return null;
	const data = err.response?.data as { message?: string; breakdown?: Record<string, number> } | undefined;
	if (!data?.message) return null;
	return { message: data.message, breakdown: data.breakdown };
}

// ─── A) Recibos ───────────────────────────────────────────────────────────────

/** Totales de recibos sin depositar de todas las cajas (agrupadora). */
export const getBranchReceiptsTotals = (): Promise<{ data: IBranchReceiptsTotalsData; cachedAt: string | null }> =>
	kpiGet<IBranchReceiptsTotalsData>('data/sales/receipts/totals');

/** Recibos pendientes de conciliar de una caja específica (paginado). */
export const getBranchReceipts = (options: {
	branch: string;
	limit?: number;
	offset?: number;
}): Promise<{ data: IBranchReceiptsData; cachedAt: string | null }> =>
	kpiGet<IBranchReceiptsData>('data/sales/receipts/by-branch', options);

// ─── B) Caja chica / caja fuerte (box=CHICA|FUERTE) ──────────────────────────

/** Balance actual de una caja física de la sucursal (por moneda). */
export const getCashBalance = (
	branch: string,
	box: CashBox = 'CHICA',
): Promise<{ data: ICashBalanceData; cachedAt: string | null }> =>
	kpiGet<ICashBalanceData>('data/sales/cash/balance', { branch, box });

/** Libro de movimientos de una caja física (filtros opcionales). */
export const getCashMovements = (options: {
	branch: string;
	box?: CashBox;
	currency?: string;
	category?: CashMovementCategory;
	from?: string;
	to?: string;
	limit?: number;
	offset?: number;
}): Promise<{ data: ICashMovementsData; cachedAt: string | null }> =>
	kpiGet<ICashMovementsData>('data/sales/cash/movements', options);

/** Registra un movimiento manual en la caja chica (o fuerte, con box=FUERTE). */
export const postCashMovement = (body: {
	branch: string;
	box?: CashBox;
	currency: string;
	type: CashMovementType;
	category: CashMovementCategory;
	amount: number;
	note?: string;
	saleOrderId?: string;
	reversesMovementId?: number;
	registeredBy: string;
}): Promise<ICashMovement> => kpiPost<ICashMovement>('data/sales/cash/movements', body);

/** Cambio de moneda en la chica: crea el par EGRESO/INGRESO CAMBIO_MONEDA. */
export const postCashExchange = (body: {
	branch: string;
	fromCurrency: string;
	fromAmount: number;
	toCurrency: string;
	toAmount: number;
	note?: string;
	registeredBy: string;
}): Promise<{ egreso: ICashMovement; ingreso: ICashMovement }> =>
	kpiPost<{ egreso: ICashMovement; ingreso: ICashMovement }>('data/sales/cash/movements/exchange', body);

// ─── C) Actas de depósito ─────────────────────────────────────────────────────

/**
 * Preview del acta (solo lectura): la ecuación resuelta (recibos + vueltos +
 * saldo de la chica) para armar los números del wizard antes del borrador.
 */
export const previewCashDeposit = (body: {
	branch: string;
	currency: string;
	receiptIds: string[];
}): Promise<ICashDepositPreview> => kpiPost<ICashDepositPreview>('data/sales/cash/deposits/preview', body);

/** Crea un BORRADOR del acta de depósito (reparto banco + caja fuerte + chica). */
export const postCashDeposit = (body: {
	branch: string;
	currency: string;
	receiptIds: string[];
	/** Monto al banco (puede ser 0 si todo va a la fuerte). */
	depositedAmount: number;
	/** Monto a la caja fuerte de la sucursal. */
	vaultAmount?: number;
	/** Vueltos (ids de movimientos) que este acta compensa. */
	changeMovementIds?: number[];
	pettyCashKept?: number;
	pettyCashWithdrawn?: number;
	bankReference?: string;
	notes?: string;
	registeredBy: string;
}): Promise<ICashDeposit> => kpiPost<ICashDeposit>('data/sales/cash/deposits', body);

/** Edita un acta en BORRADOR (montos/reparto/recibos/vueltos). El API re-valida la ecuación. */
export const patchCashDeposit = (
	depositId: number | string,
	body: {
		receiptIds?: string[];
		depositedAmount?: number;
		vaultAmount?: number;
		changeMovementIds?: number[];
		pettyCashKept?: number;
		pettyCashWithdrawn?: number;
		bankReference?: string;
		notes?: string;
		registeredBy: string;
	},
): Promise<ICashDeposit> => kpiPatch<ICashDeposit>(`data/sales/cash/deposits/${depositId}`, body);

/** Lista de actas de depósito (paginado, filtros opcionales). */
export const getCashDeposits = (options?: {
	branch?: string;
	status?: DepositStatus;
	limit?: number;
	offset?: number;
}): Promise<{ data: ICashDepositsData; cachedAt: string | null }> =>
	kpiGet<ICashDepositsData>('data/sales/cash/deposits', options);

/** Detalle/reporte completo de un acta de depósito. */
export const getCashDeposit = (depositId: number | string): Promise<{ data: ICashDeposit; cachedAt: string | null }> =>
	kpiGet<ICashDeposit>(`data/sales/cash/deposits/${depositId}`);

/** Confirma un acta de depósito (BORRADOR → CONFIRMADO). */
export const confirmCashDeposit = (
	depositId: number | string,
	body: { registeredBy: string; bankReference?: string },
): Promise<ICashDeposit> => kpiPost<ICashDeposit>(`data/sales/cash/deposits/${depositId}/confirm`, body);

/** Anula un acta de depósito (genera contra-movimientos, nada se borra). */
export const annulCashDeposit = (
	depositId: number | string,
	body: { registeredBy: string; reason: string },
): Promise<ICashDeposit> => kpiPost<ICashDeposit>(`data/sales/cash/deposits/${depositId}/annul`, body);

// ─── D) Objetivos de caja chica ───────────────────────────────────────────────

/** Objetivos de monto por caja/moneda. */
export const getCashTargets = async (branch?: string): Promise<{ data: ICashTarget[]; cachedAt: string | null }> => {
	const result = await kpiGet<{ targets: ICashTarget[] }>('data/sales/cash/targets', branch ? { branch } : undefined);
	return { data: result.data.targets ?? [], cachedAt: result.cachedAt };
};

/** Actualiza el objetivo de una caja/moneda. */
export const putCashTarget = (body: {
	branch: string;
	currency: string;
	targetAmount: number;
	registeredBy: string;
}): Promise<ICashTarget> => kpiPut<ICashTarget>('data/sales/cash/targets', body);
